import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Camera, CheckCircle, Clock, UserCheck } from 'lucide-react'
import { publicSignInApi } from '@/api/signin'
import { shrinkImage } from './shrinkImage'
import { suggestEmail } from './emailTypos'

const REMEMBER_KEY = 'certslate-signin-details'

function remembered(): { firstName: string; lastName: string; email: string } {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY)
    if (raw) {
      const v = JSON.parse(raw)
      return { firstName: String(v.firstName ?? ''), lastName: String(v.lastName ?? ''), email: String(v.email ?? '') }
    }
  } catch { /* private window or blocked storage: start empty */ }
  return { firstName: '', lastName: '', email: '' }
}

function errorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

const field = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent'

/**
 * Where an attendee lands after scanning the QR code on the trainer's screen: a name, an email and (when the
 * organisation asks for one) a selfie. No account. It deliberately never says whether they were found on the
 * register: that is for the trainer to sort out, and telling anyone with the link who is booked on would leak it.
 */
export default function PublicSignInPage() {
  const { code = '' } = useParams<{ code: string }>()
  const info = useQuery({ queryKey: ['public-signin', code], queryFn: () => publicSignInApi.info(code), retry: false })

  const initial = useRef(remembered()).current
  const [firstName, setFirstName] = useState(initial.firstName)
  const [lastName, setLastName] = useState(initial.lastName)
  const [email, setEmail] = useState(initial.email)
  const [selfie, setSelfie] = useState<Blob | null>(null)
  const [selfieUrl, setSelfieUrl] = useState<string | null>(null)
  const [cannotPhoto, setCannotPhoto] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [closed, setClosed] = useState(false)
  const camera = useRef<HTMLInputElement>(null)

  const colour = info.data?.primaryColour ?? '#4F46E5'
  const photoMode = info.data?.photoMode ?? 'none'
  const suggestion = suggestEmail(email)
  const ring = { ['--tw-ring-color' as string]: colour }

  async function takePhoto(file?: File) {
    if (!file) return
    const small = await shrinkImage(file)
    setSelfie(small)
    setCannotPhoto(false)
    setSelfieUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(small) })
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (photoMode === 'required' && !selfie && !cannotPhoto) {
      setError('Please take a photo of yourself, or tick the box if you can’t.')
      return
    }
    setBusy(true)
    try {
      await publicSignInApi.submit(code, { firstName, lastName, email, selfie, couldNotTakePhoto: cannotPhoto })
      try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ firstName, lastName, email })) } catch { /* fine */ }
      setDone(true)
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 410) setClosed(true)
      else setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8f9fc' }}>
      <header className="shadow-sm" style={{ backgroundColor: colour }}>
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          {info.data?.logoUrl && <img src={info.data.logoUrl} alt="" className="h-9 w-auto object-contain shrink-0" />}
          <span className="text-white font-bold text-lg leading-tight">{info.data?.organisation ?? 'Sign in'}</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-6">
        {info.isLoading && <p className="text-center text-gray-500 mt-10">Loading…</p>}

        {info.isError && (
          <div className="rounded-xl bg-white border border-gray-200 p-6 text-center">
            <h1 className="text-lg font-semibold text-gray-900">This link isn&apos;t valid</h1>
            <p className="text-sm text-gray-600 mt-2">Check you scanned the right code, or ask your trainer.</p>
          </div>
        )}

        {info.data && (!info.data.open || closed) && !done && (
          <div className="rounded-xl bg-white border border-gray-200 p-6 text-center">
            <Clock className="mx-auto text-gray-400" size={28} />
            <h1 className="text-lg font-semibold text-gray-900 mt-3">Sign-in has closed</h1>
            <p className="text-sm text-gray-600 mt-2">This sign-in isn&apos;t open any more. If you&apos;re at the course, let your trainer know.</p>
          </div>
        )}

        {info.data && done && (
          <div className="rounded-xl bg-white border border-gray-200 p-6 text-center">
            <CheckCircle className="mx-auto text-green-600" size={36} />
            <h1 className="text-lg font-semibold text-gray-900 mt-3">Thanks, {firstName.trim()}</h1>
            <p className="text-sm text-gray-600 mt-2">Your sign-in has been sent to your trainer. You can close this page.</p>
          </div>
        )}

        {info.data && info.data.open && !closed && !done && (
          <form onSubmit={submit} className="rounded-xl bg-white border border-gray-200 p-5 space-y-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><UserCheck size={18} style={{ color: colour }} /> Sign in</h1>
              <p className="text-sm text-gray-500 mt-0.5">{info.data.title}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">First name</span>
                <input className={field} style={ring} value={firstName} onChange={e => setFirstName(e.target.value)}
                  autoComplete="given-name" maxLength={80} required />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Last name</span>
                <input className={field} style={ring} value={lastName} onChange={e => setLastName(e.target.value)}
                  autoComplete="family-name" maxLength={80} required />
              </label>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Email</span>
              <input className={field} style={ring} type="email" inputMode="email" value={email}
                onChange={e => setEmail(e.target.value)} autoComplete="email" autoCapitalize="none" maxLength={254} required />
              <span className="text-xs text-gray-500">Use the email you booked with, if you can.</span>
              {suggestion && (
                <button type="button" className="block text-sm mt-1 underline" style={{ color: colour }} onClick={() => setEmail(suggestion)}>
                  Did you mean {suggestion}?
                </button>
              )}
            </label>

            {photoMode !== 'none' && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">
                  Photo {photoMode === 'optional' && <span className="font-normal text-gray-400">(optional)</span>}
                </span>
                <div className="flex items-center gap-3">
                  {selfieUrl
                    ? <img src={selfieUrl} alt="Your photo" className="h-16 w-16 rounded-full object-cover border border-gray-200" />
                    : <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><Camera size={22} /></div>}
                  <button type="button" onClick={() => camera.current?.click()}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    {selfie ? 'Retake' : 'Take a photo'}
                  </button>
                  <input ref={camera} type="file" accept="image/*" capture="user" className="hidden"
                    onChange={e => { void takePhoto(e.target.files?.[0]); e.target.value = '' }} />
                </div>
                {photoMode === 'required' && !selfie && (
                  <label className="flex items-start gap-2 text-sm text-gray-600">
                    <input type="checkbox" className="mt-0.5" checked={cannotPhoto} onChange={e => setCannotPhoto(e.target.checked)} />
                    I can&apos;t take a photo right now
                  </label>
                )}
                {info.data.photoNotice && <p className="text-xs text-gray-500">{info.data.photoNotice}</p>}
              </div>
            )}

            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

            <button type="submit" disabled={busy}
              className="w-full rounded-lg px-4 py-3 text-base font-semibold text-white disabled:opacity-60" style={{ backgroundColor: colour }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <p className="text-xs text-gray-500">
              Your name{photoMode !== 'none' ? ', email and photo are' : ' and email are'} shared with {info.data.organisation}, who are running this
              course, to record that you attended.
            </p>
          </form>
        )}
      </main>
    </div>
  )
}
